// Command-bar (cmd/ctrl+K). Snelle navigatie + fuzzy search over modules,
// orders, products, customers, taken, marketplaces.

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { fetchCustomers, fetchOrders, fetchProducts, fetchTasks } from "../api/client";
import { useAuth } from "../context/AuthContext";

interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  group: string;
  to: string;
  perm?: string;
}

const STATIC_NAV: CommandItem[] = [
  { id: "nav:dashboard", group: "Navigatie", label: "Dashboard", to: "/dashboard", perm: "dashboard.read" },
  { id: "nav:orders", group: "Navigatie", label: "Orders", to: "/orders", perm: "orders.read" },
  { id: "nav:warehouse", group: "Navigatie", label: "Warehouse", to: "/warehouse", perm: "voorraad.read" },
  { id: "nav:producten", group: "Navigatie", label: "Producten", to: "/producten", perm: "products.catalog_view" },
  { id: "nav:inkoop", group: "Navigatie", label: "Inkoop", to: "/inkoop", perm: "inkoop.read" },
  { id: "nav:verkopen", group: "Navigatie", label: "Verkopen", to: "/verkopen", perm: "amazon.read" },
  { id: "nav:beheer-marketplaces", group: "Navigatie", label: "Beheer · Marketplaces", to: "/beheer/marketplaces", perm: "amazon.read" },
  { id: "nav:klanten", group: "Navigatie", label: "Klanten", to: "/klanten", perm: "customers.view" },
  { id: "nav:finance", group: "Navigatie", label: "Finance", to: "/finance", perm: "finance.read" },
  { id: "nav:btw-aangifte", group: "Navigatie", label: "BTW-aangifte", to: "/finance/btw-aangifte", perm: "finance.read" },
  { id: "nav:rapportages", group: "Navigatie", label: "Rapportages", to: "/rapportages", perm: "reports.view" },
  { id: "nav:taken", group: "Navigatie", label: "Taken", to: "/taken", perm: "tasks.read" },
  { id: "nav:reconciliation", group: "Navigatie", label: "Reconciliation", to: "/reconciliation", perm: "reconcile.status" },
  { id: "nav:beheer", group: "Navigatie", label: "Beheer", to: "/beheer", perm: "users.read" },
  { id: "nav:progress", group: "Navigatie", label: "Project voortgang", to: "/progress" },
];

const QUICK_ACTIONS: CommandItem[] = [
  { id: "act:nieuwe-taak", group: "Snelle acties", label: "+ Nieuwe taak", to: "/taken/nieuw", perm: "tasks.write" },
];

export function CommandBar() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(0);
  const [dynamicItems, setDynamicItems] = useState<CommandItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { hasPerm } = useAuth();

  // Global hotkey
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setQ("");
      setSelected(0);
      setDynamicItems([]);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Debounced dynamic search
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) { setDynamicItems([]); return; }
    const t = setTimeout(async () => {
      const results: CommandItem[] = [];
      const calls: Promise<void>[] = [];
      if (hasPerm("orders.read")) {
        calls.push(fetchOrders({ q: term, limit: 6 }).then((rs) => {
          for (const o of rs) results.push({
            id: `order:${o.id}`, group: "Orders",
            label: `${o.external_id ?? o.id}`,
            hint: `${o.sales_channel_name ?? "—"} · ${o.workflow_state ?? o.status ?? ""}`,
            to: `/orders/${o.id}`,
          });
        }).catch(() => {}));
      }
      if (hasPerm("products.catalog_view")) {
        calls.push(fetchProducts(term).then((rs) => {
          for (const p of rs.slice(0, 6)) if (p.sku) results.push({
            id: `prod:${p.sku}`, group: "Producten",
            label: p.sku, hint: p.title ?? undefined,
            to: `/producten/${encodeURIComponent(p.sku)}`,
          });
        }).catch(() => {}));
      }
      if (hasPerm("customers.view")) {
        calls.push(fetchCustomers(term).then((rs) => {
          for (const c of rs.slice(0, 4)) results.push({
            id: `cust:${c.email_hash}`, group: "Klanten",
            label: c.email_hash.slice(0, 14) + "…",
            hint: `${c.country ?? "—"} · ${c.order_count} orders`,
            to: `/klanten/${encodeURIComponent(c.email_hash)}`,
          });
        }).catch(() => {}));
      }
      if (hasPerm("tasks.read")) {
        calls.push(fetchTasks({ q: term }).then((rs) => {
          for (const t of rs.slice(0, 6)) results.push({
            id: `task:${t.id}`, group: "Taken",
            label: t.title, hint: `#${t.id} · ${t.status}`,
            to: `/taken/${t.id}`,
          });
        }).catch(() => {}));
      }
      await Promise.all(calls);
      setDynamicItems(results);
    }, 200);
    return () => clearTimeout(t);
  }, [q, open, hasPerm]);

  const items = useMemo(() => {
    const term = q.trim().toLowerCase();
    const staticAll = [...QUICK_ACTIONS, ...STATIC_NAV].filter((i) => !i.perm || hasPerm(i.perm));
    const filteredStatic = term
      ? staticAll.filter((i) => i.label.toLowerCase().includes(term))
      : staticAll;
    return [...filteredStatic, ...dynamicItems];
  }, [q, dynamicItems, hasPerm]);

  // Reset selected when items shrink
  useEffect(() => { if (selected >= items.length) setSelected(0); }, [items.length, selected]);

  const choose = (item: CommandItem) => {
    setOpen(false);
    navigate(item.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSelected((s) => Math.min(items.length - 1, s + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSelected((s) => Math.max(0, s - 1)); }
    else if (e.key === "Enter" && items[selected]) { e.preventDefault(); choose(items[selected]!); }
  };

  if (!open) return null;

  // Group items in display
  const grouped: Array<{ group: string; items: CommandItem[] }> = [];
  for (const it of items) {
    const last = grouped[grouped.length - 1];
    if (last && last.group === it.group) last.items.push(it);
    else grouped.push({ group: it.group, items: [it] });
  }

  let flatIdx = -1;

  return createPortal(
    <div className="cmdk-backdrop" onClick={() => setOpen(false)}>
      <div className="cmdk-modal" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="text"
          className="cmdk-input"
          placeholder="Zoek of navigeer… (orders, producten, klanten, taken)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <div className="cmdk-results">
          {items.length === 0 ? (
            <div className="cmdk-empty">Geen resultaten</div>
          ) : grouped.map((g) => (
            <div key={g.group}>
              <div className="cmdk-group-label">{g.group}</div>
              {g.items.map((it) => {
                flatIdx++;
                const isSel = flatIdx === selected;
                return (
                  <div
                    key={it.id}
                    className={"cmdk-item" + (isSel ? " selected" : "")}
                    onMouseEnter={() => setSelected(flatIdx)}
                    onClick={() => choose(it)}
                  >
                    <span className="cmdk-label">{it.label}</span>
                    {it.hint && <span className="cmdk-hint">{it.hint}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="cmdk-footer">
          <span>↑↓ navigeer</span>
          <span>↵ open</span>
          <span>esc sluit</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
